import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type ExecutivePosture = {
  generatedAt?: string
  overallOperationalPressure?: number
  governanceStability?: number
  certificationRiskLevel?: string
  readinessTrajectory?: string
  unstableWorkflowCount?: number
  degradedControlCount?: number
  staleEvidenceCount?: number
  blockerConcentration?: number
  highRiskSystemCount?: number
  lastComputedAt?: string
  lastSnapshotId?: string | null
  lastSnapshotGeneratedAt?: string
  snapshotInserted?: boolean | null
  dedupReason?: string | null
  explanations?: {
    summary?: string
    lines?: Array<{ code: string; message: string; linkedEvents?: Array<{ id: string; eventType: string }> }>
    recentOperationalEvents?: Array<{ id: string; eventType: string; severity?: string; createdAt?: string }>
  }
  stability?: { level: string; score: number; factors?: Array<{ code: string; count: number }> }
  trajectory?: { trajectory: string; deltaScore?: number }
  blastRadius?: { impacts: Array<{ controlCode: string; summary: string; standardCount: number; systemCount: number; workflowCount: number }> }
  instability?: { instabilityIndex: number; band: string; signals?: Array<{ code: string; count: number }> }
  pressure?: {
    mostDegradedStandards?: Array<{ standardCode: string; totalPressure: number }>
    pressureConcentration?: { standardCode: string; totalPressure: number }
  }
  graphOverlay?: { nodes: unknown[]; edges: unknown[] }
}

export type ExecutiveStandardMiniCard = {
  standardCode: string
  standardName?: string
  hasEnoughHistory: boolean
  certificationRiskLevel?: string
  readinessTrajectory?: string
  operationalPressure?: number
  degradedControlCount?: number
  blockerCount?: number
  staleEvidenceCount?: number
}

export type ExecutiveStandardsSummary = {
  cards: ExecutiveStandardMiniCard[]
  hasOrgHistory?: boolean
  generatedAt?: string
}

export type StandardExecutiveDrilldown = {
  standardCode: string
  standardName?: string
  certificationRiskLevel?: string
  readinessTrajectory?: string
  overallOperationalPressure?: number
  stability?: ExecutivePosture['stability']
  trajectory?: ExecutivePosture['trajectory']
  pressure?: ExecutivePosture['pressure']
  blastRadius?: ExecutivePosture['blastRadius']
  degradedControls?: Array<{
    id: string
    controlCode?: string
    controlHealth: string
    operationalPressure: number
    informationSystemName?: string
  }>
  impactedSystems?: Array<{ informationSystemId: string; name?: string; pressure: number; degradedCount: number }>
  recentOperationalEvents?: Array<{ id: string; eventType: string; severity?: string; createdAt?: string }>
  explanations?: ExecutivePosture['explanations']
  blockerConcentration?: number
}

export function executiveGovernanceStandardPath(standardCode: string): string {
  return `/compliance-center/executive-governance/${encodeURIComponent(standardCode)}`
}

export async function fetchExecutiveOverview() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_OVERVIEW), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as ExecutivePosture
}

export async function fetchExecutiveHistory(standardCode?: string, limit = 20) {
  const q = new URLSearchParams()
  if (standardCode) q.set('standardCode', standardCode)
  q.set('limit', String(limit))
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.EXECUTIVE_GOVERNANCE_HISTORY}?${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    history: Array<{
      id: string
      generatedAt: string
      certificationRiskLevel: string
      readinessTrajectory: string
      overallOperationalPressure: number
    }>
    stabilityTrend: Array<{ at: string; level: string }>
    pressureTrend: Array<{ at: string; pressure: number }>
  }
}

export async function fetchExecutiveStandardsSummary(standardCodes: string[]) {
  const q = new URLSearchParams()
  if (standardCodes.length) q.set('standards', standardCodes.join(','))
  const base = API_ENDPOINTS.EXECUTIVE_GOVERNANCE_STANDARDS_SUMMARY
  const url = q.toString() ? `${base}?${q}` : base
  const res = await fetch(withOrganizationScope(url), { headers: getAuthHeaders() })
  return parseJson(res) as ExecutiveStandardsSummary
}

export async function fetchStandardExecutiveDrilldown(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_STANDARD(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as StandardExecutiveDrilldown
}

export async function fetchCertificationStability() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_STABILITY), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchReadinessTrajectory() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_TRAJECTORY), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchExecutivePressure() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_PRESSURE), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchBlastRadius() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_BLAST_RADIUS), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchGovernanceInstability() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_INSTABILITY), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function recomputeExecutiveGovernance() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_RECOMPUTE), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    snapshotInserted?: boolean
    dedupReason?: string
    diagnostics?: {
      lastComputedAt?: string
      lastSnapshotId?: string
      snapshotInserted?: boolean
      dedupReason?: string
    }
  }
}

export async function fetchSystemExecutiveContribution(informationSystemId: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.EXECUTIVE_GOVERNANCE_SYSTEM(informationSystemId)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    executiveCriticality: string
    governancePressure: number
    blastRadiusContribution: Array<{ controlCode: string; summary: string }>
    certificationInstabilityContribution: number
  }
}
